import React, { useEffect, useState } from 'react';
import { Button } from '../../../components/ui';
import DateTimeInput from '../../../components/ui/DateTimeInput';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

export function defaultScheduleSendAt(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(0);
  d.setHours(d.getHours() + 1);
  if (d.getTime() <= Date.now()) {
    d.setHours(d.getHours() + 1);
  }
  return d.toISOString();
}

interface ComposeScheduleSendModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (scheduledSendAt: string) => void;
  isSubmitting?: boolean;
}

const ComposeScheduleSendModal: React.FC<ComposeScheduleSendModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
}) => {
  const { colors } = useTheme();
  const [scheduledAt, setScheduledAt] = useState(defaultScheduleSendAt);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setScheduledAt(defaultScheduleSendAt());
      setError(null);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (!scheduledAt) {
      setError('Choose a date and time.');
      return;
    }
    const when = new Date(scheduledAt);
    if (Number.isNaN(when.getTime())) {
      setError('Invalid date and time.');
      return;
    }
    if (when.getTime() <= Date.now()) {
      setError('Scheduled time must be in the future.');
      return;
    }
    onConfirm(when.toISOString());
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalHeader>
        <ModalTitle>Schedule send</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <p className={cn('text-sm mb-4', colors.textMuted)}>
          The message will stay in Outbox until the scheduled time. You can cancel or reschedule from there
          before it sends.
        </p>
        <DateTimeInput
          value={scheduledAt}
          onChange={(e) => {
            setScheduledAt(e.target.value);
            setError(null);
          }}
          minDate={new Date().toISOString()}
          showQuickFill={false}
          helperText="Local date and time"
          error={error ?? undefined}
        />
      </ModalContent>
      <ModalFooter className="flex flex-wrap gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="button" onClick={handleConfirm} loading={isSubmitting} disabled={isSubmitting}>
          Schedule send
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ComposeScheduleSendModal;
